import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FxSavingsWidget from "@/components/FxSavingsWidget";
import FloLogo from "@/components/FloLogo";
import RbiBadge from "@/components/RbiBadge";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devHint, setDevHint] = useState("");
  const [devCode, setDevCode] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  // Capture inbound ?ref= for attribution (persist across the OTP round-trip).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("flo_ref", ref.trim().toUpperCase().slice(0, 16));
  }, []);

  const requestOtp = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email: email.trim().toLowerCase() });
      if (data.dev_hint) setDevHint(data.dev_hint);
      if (data.dev_code) setDevCode(data.dev_code);
      setStep("otp");
      toast.success("Code sent to your email");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg =
        typeof raw === "string"
          ? raw
          : Array.isArray(raw)
            ? raw[0]?.msg || "Please enter a valid email address."
            : "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError("");
    if (code.length !== 6) {
      setError("Please enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const ref = localStorage.getItem("flo_ref") || undefined;
      const { data } = await api.post("/auth/verify-otp", {
        email: email.trim().toLowerCase(),
        code,
        ...(ref ? { ref } : {}),
      });
      // Attribution consumed — clear so it doesn't apply to future accounts on this device.
      if (ref) localStorage.removeItem("flo_ref");
      login(data.token, data.user);
      toast.success("Welcome to Flo Payments");
      // Admin accounts skip both onboarding and the user dashboard — deep-link
      // to /admin so the very first thing they see after OTP verify is the
      // admin console, not the exporter dashboard.
      const target = data.user?.is_admin
        ? "/admin"
        : data.onboarded
          ? "/dashboard"
          : "/onboarding";
      navigate(target, { replace: true });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg =
        typeof raw === "string"
          ? raw
          : Array.isArray(raw)
            ? raw[0]?.msg || "Verification failed."
            : "Verification failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 md:p-14">
        <a
          href="https://useflopayments.com"
          className="flex items-center gap-3 group"
          data-testid="login-logo-home-link"
          aria-label="Flo Payments — go to marketing site"
        >
          <FloLogo variant="lockup-light" size={40} className="group-hover:opacity-80 transition-opacity" />
        </a>

        <div className="max-w-md mx-auto w-full">
          {step === "email" && (
            <div>
              <RbiBadge className="mb-8" />
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-primary mb-3">
                Cross-border payments for Indian goods exporters
              </p>
              <h1 className="font-display text-4xl md:text-5xl font-extrabold text-foreground tracking-tight mb-3">
                Receive international payments as an Indian goods exporter.
              </h1>
              <p className="text-muted-foreground text-base md:text-lg mb-2 leading-relaxed">
                Faster payments, better rates than your bank — every time.
              </p>
              <p className="text-muted-foreground text-sm md:text-base mb-10 leading-relaxed">
                Built for Indian businesses that export goods overseas. USD · GBP · EUR.
              </p>

              <form onSubmit={requestOtp} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@yourcompany.in"
                    className="h-14 text-base"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={AUTH.emailInput}
                  />
                </div>

                {error && (
                  <div className="text-sm text-destructive" data-testid={AUTH.authError}>
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-14 text-base font-semibold rounded-lg"
                  disabled={loading}
                  data-testid={AUTH.continueBtn}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending code…
                    </>
                  ) : (
                    <>
                      Continue <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Regulated banking infrastructure &middot; Bank-grade security &middot; Transparent, verifiable FX rates
              </div>
            </div>
          )}

          {step === "otp" && (
            <div>
              <RbiBadge className="mb-8" />
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError("");
                }}
                className="flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Use a different email
              </button>

              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight mb-3">
                Check your email.
              </h1>
              <p className="text-muted-foreground text-base mb-8 leading-relaxed">
                We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>. It expires in 10 minutes.
              </p>

              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-3 block">
                    Enter code
                  </Label>
                  <InputOTP
                    maxLength={6}
                    value={code}
                    onChange={(v) => setCode(v)}
                    data-testid={AUTH.otpInput}
                    containerClassName="justify-start"
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="h-14 w-12 text-xl font-semibold" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {error && (
                  <div className="text-sm text-destructive" data-testid={AUTH.authError}>
                    {error}
                  </div>
                )}

                {devHint && (
                  <div
                    className="rounded-lg border border-[#C77F1E]/40 bg-[#FBF1E1] p-4"
                    data-testid={AUTH.devHint}
                  >
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#8F5A16] mb-2">
                      Demo mode
                    </p>
                    <p className="text-xs text-[#8F5A16] mb-3 leading-relaxed">{devHint}</p>
                    {devCode && (
                      <div className="flex items-center justify-between gap-3 bg-white border border-[#C77F1E]/30 rounded-lg px-4 py-3">
                        <span className="font-display text-2xl font-extrabold tracking-[0.4em] text-primary sf-mono" data-testid="login-dev-code">
                          {devCode}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setCode(devCode);
                            toast.success("Code filled");
                          }}
                          className="text-xs font-semibold text-primary hover:underline whitespace-nowrap"
                          data-testid="login-dev-code-autofill"
                        >
                          Auto-fill
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <Button
                  onClick={verifyOtp}
                  className="w-full h-14 text-base font-semibold rounded-lg"
                  disabled={loading || code.length !== 6}
                  data-testid={AUTH.otpSubmitBtn}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Verifying…
                    </>
                  ) : (
                    <>Verify &amp; continue</>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={requestOtp}
                  className="text-sm text-primary hover:underline"
                  data-testid={AUTH.otpResendBtn}
                >
                  Resend code
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          By continuing you agree to receive transactional emails from Flo Payments.
        </p>
      </div>

      {/* Right: savings calculator on brand background */}
      <div className="relative hidden lg:block overflow-hidden bg-primary">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-[#164AB0]" />
        <div className="relative h-full flex flex-col justify-center p-14">
          <div className="max-w-md w-full mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-xs font-medium mb-6 border border-white/20 text-white">
              <span className="h-2 w-2 rounded-full bg-[#1E8A5B]" /> See your savings — live
            </div>
            <FxSavingsWidget variant="onDark" />
          </div>
        </div>
      </div>
    </div>
  );
}
